document.addEventListener('DOMContentLoaded', () => {
  if (window._bstSectionMainPageInitialized) return;
  window._bstSectionMainPageInitialized = true;

  const STOCKIST_SOURCE_PAGE_CANDIDATES = ['/pages/filialen', '/pages/store-locator'];

  const getStickyOffset = () => {
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    if (!headerGroup) return 24;
    return Math.max(24, Math.round(headerGroup.getBoundingClientRect().height + 16));
  };

  const scrollToElement = (element) => {
    const offset = getStickyOffset();
    const top = window.scrollY + element.getBoundingClientRect().top - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth'
    });
  };

  const ensureStockistScript = (src) => {
    if (!src) return;
    if (document.querySelector(`script[src="${src}"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.body.appendChild(script);
  };

  const getStockistSourceCandidates = () => {
    const configuredCandidates = (document.querySelector('.bst-main-page')?.dataset.stockistSourcePages || '')
      .split(',')
      .map((candidate) => candidate.trim())
      .filter(Boolean);

    return [...new Set([...configuredCandidates, ...STOCKIST_SOURCE_PAGE_CANDIDATES])];
  };

  const fetchStockistSourceMarkup = async () => {
    for (const path of getStockistSourceCandidates()) {
      const response = await fetch(path, { credentials: 'same-origin' });
      if (!response.ok) continue;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const stockistWidget = parsed.querySelector('stockist-store-locator');
      const stockistScript = parsed.querySelector('script[src*="stockist.co/embed/"]');

      if (stockistWidget) {
        return { stockistWidget, stockistScript };
      }
    }

    throw new Error('Stockist widget markup missing on configured source pages');
  };

  const hydrateLegacyStockist = async () => {
    if (window.__bstStockistInstalled) return;

    const syncedPage = document.querySelector('.bst-main-page');
    if (!syncedPage) return;

    const legacyLocator = syncedPage.querySelector('[data-bst-stockist-mount], store-locator[data-component]');
    if (!legacyLocator) return;

    const locatorSection = legacyLocator.closest('.section--store-locator');
    const locatorWrapper =
      locatorSection?.querySelector('.section__wrapper') ||
      locatorSection?.querySelector('.section__content') ||
      legacyLocator.parentElement;

    if (!locatorSection || !locatorWrapper) return;

    locatorSection.classList.add('bst-stockist-loading');

    try {
      const { stockistWidget, stockistScript } = await fetchStockistSourceMarkup();

      const widgetShell = document.createElement('div');
      widgetShell.className = 'bst-stockist-locator';
      widgetShell.appendChild(stockistWidget.cloneNode(true));

      if (legacyLocator.matches('store-locator[data-component]')) {
        legacyLocator.replaceWith(widgetShell);
      } else {
        legacyLocator.replaceChildren(widgetShell);
      }

      ensureStockistScript(stockistScript?.src);

      locatorSection.classList.remove('bst-stockist-loading');
      locatorSection.classList.add('bst-stockist-ready');
    } catch (error) {
      console.error('Failed to hydrate Stockist locator on synced page', error);
      locatorSection.classList.remove('bst-stockist-loading');
      locatorSection.classList.add('bst-stockist-error');
    }
  };

  hydrateLegacyStockist();

  document.querySelectorAll('.bst-main-page__content .section--accordion .accordion__inner').forEach((group) => {
    const sections = [...group.querySelectorAll(':scope > .accordion__section')];
    const interactiveSections = [];

    sections.forEach((section) => {
      const title = [...section.children].find((child) => child.classList?.contains('accordion__title'));
      const bodies = [...section.querySelectorAll('.accordion__item__body')];
      const itemHeads = [...section.querySelectorAll('accordion > .accordion__item > .accordion__item__head')];
      const hasMeaningfulItemHeads = itemHeads.some((head) => head.textContent.trim().length > 0);

      // When each item has its own subtitle, make the section h3 also trigger the first accordion item
      if (hasMeaningfulItemHeads && title && itemHeads.length > 0) {
        const firstHead = itemHeads[0];
        title.style.cursor = 'pointer';
        title.addEventListener('click', () => firstHead.click());
        title.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          firstHead.click();
        });
      }

      if (!title || bodies.length === 0 || hasMeaningfulItemHeads) return;

      section.classList.add('bst-synced-accordion-section');
      interactiveSections.push(section);
      title.setAttribute('role', 'button');
      title.setAttribute('tabindex', '0');
      title.setAttribute('aria-expanded', 'false');

      bodies.forEach((body) => {
        body.hidden = true;
      });

      const setOpen = (open) => {
        section.classList.toggle('is-open', open);
        title.setAttribute('aria-expanded', open ? 'true' : 'false');
        bodies.forEach((body) => {
          body.hidden = !open;
        });
      };

      title.addEventListener('click', () => {
        const nextState = !section.classList.contains('is-open');

        sections.forEach((otherSection) => {
          if (otherSection === section) return;
          otherSection.classList.remove('is-open');
          const otherTitle = [...otherSection.children].find((child) => child.classList?.contains('accordion__title'));
          if (otherTitle) otherTitle.setAttribute('aria-expanded', 'false');
          otherSection.querySelectorAll('.accordion__item__body').forEach((body) => {
            body.hidden = true;
          });
        });

        setOpen(nextState);
      });

      title.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        title.click();
      });
    });

    const sectionContainer = group.closest('.section--accordion');
    const allAnchorLinks = sectionContainer?.querySelectorAll('.js-anchor-link[data-link-id]') || [];

    const setActiveLink = (activeLink) => {
      allAnchorLinks.forEach((link) => link.classList.toggle('is-active', link === activeLink));
    };

    allAnchorLinks.forEach((link) => {
      const targetId = link.getAttribute('data-link-id')?.trim();
      if (!targetId) return;

      link.setAttribute('href', `#${encodeURIComponent(targetId)}`);

      link.addEventListener('click', (event) => {
        // Find the target year-section by data-id, regardless of whether it
        // is "interactive" (i.e. has no subtitle h4) — we always want to scroll.
        const targetSection = sections.find(
          (section) => section.getAttribute('data-id')?.trim() === targetId
        );

        if (!targetSection) return;

        event.preventDefault();
        setActiveLink(link);

        // If the section uses the synced/interactive accordion, also open it.
        if (interactiveSections.includes(targetSection) && !targetSection.classList.contains('is-open')) {
          const targetTitle = [...targetSection.children].find((child) =>
            child.classList?.contains('accordion__title')
          );
          if (targetTitle) targetTitle.click();
        }

        requestAnimationFrame(() => {
          const stickyNav = sectionContainer?.querySelector('[data-bst-accordion-nav]');
          const navHeight = stickyNav ? stickyNav.getBoundingClientRect().height : 0;
          const extraOffset = getStickyOffset() + navHeight;
          const top = window.scrollY + targetSection.getBoundingClientRect().top - extraOffset;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });

        if (history.replaceState) {
          history.replaceState(null, '', `#${encodeURIComponent(targetId)}`);
        }
      });
    });

    // Scroll-based active link highlighting via IntersectionObserver
    if (allAnchorLinks.length > 0) {
      const anchorSections = sections.filter((s) => s.getAttribute('data-id'));
      const scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const id = entry.target.getAttribute('data-id');
            const matchingLink = [...allAnchorLinks].find(
              (link) => link.getAttribute('data-link-id') === id
            );
            if (matchingLink) setActiveLink(matchingLink);
          });
        },
        { rootMargin: '-10% 0px -70% 0px', threshold: 0 }
      );
      anchorSections.forEach((s) => scrollObserver.observe(s));
    }
  });

  document.querySelectorAll('.bst-main-page__content .section--accordion accordion').forEach((accordion) => {
    const items = [...accordion.querySelectorAll(':scope > .accordion__item')];
    accordion.classList.add('bst-animated-accordion');

    const stopAnimation = (body) => {
      if (body._bstAccordionEndHandler) {
        body.removeEventListener('transitionend', body._bstAccordionEndHandler);
        delete body._bstAccordionEndHandler;
      }
    };

    const closeItem = (item) => {
      item.classList.remove('is-open');
      const head = item.querySelector(':scope > .accordion__item__head');
      const body = item.querySelector(':scope > .accordion__item__body');
      if (head) head.setAttribute('aria-expanded', 'false');
      if (!body) return;
      if (body.hidden) return;

      stopAnimation(body);

      const startHeight = `${body.scrollHeight}px`;
      body.hidden = false;
      body.style.height = startHeight;
      body.style.opacity = '1';

      requestAnimationFrame(() => {
        body.style.height = '0px';
        body.style.opacity = '0';
      });

      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'height') return;
        body.hidden = true;
        body.style.height = '';
        stopAnimation(body);
      };

      body._bstAccordionEndHandler = onTransitionEnd;
      body.addEventListener('transitionend', onTransitionEnd);
    };

    const openItem = (item) => {
      item.classList.add('is-open');
      const head = item.querySelector(':scope > .accordion__item__head');
      const body = item.querySelector(':scope > .accordion__item__body');
      if (head) head.setAttribute('aria-expanded', 'true');
      if (!body) return;

      stopAnimation(body);

      body.hidden = false;
      body.style.height = '0px';
      body.style.opacity = '0';

      const targetHeight = `${body.scrollHeight}px`;

      requestAnimationFrame(() => {
        body.style.height = targetHeight;
        body.style.opacity = '1';
      });

      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'height') return;
        body.style.height = 'auto';
        stopAnimation(body);
      };

      body._bstAccordionEndHandler = onTransitionEnd;
      body.addEventListener('transitionend', onTransitionEnd);
    };

    items.forEach((item) => {
      const head = item.querySelector(':scope > .accordion__item__head');
      const body = item.querySelector(':scope > .accordion__item__body');

      if (!head || !body) return;
      if (head.textContent.trim().length === 0 && head.children.length === 0) {
        head.classList.add('is-empty');
      }

      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');
      head.setAttribute('aria-expanded', 'false');
      body.hidden = true;
      body.style.opacity = '0';

      const toggleItem = () => {
        const shouldOpen = body.hidden;

        items.forEach((otherItem) => {
          if (otherItem !== item) closeItem(otherItem);
        });

        if (shouldOpen) {
          openItem(item);
        } else {
          closeItem(item);
        }
      };

      head.addEventListener('click', toggleItem);
      head.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggleItem();
      });
    });
  });

  /* ---- RTE gallery lightbox --------------------------------------------
     Turns the migrated `.bst-rte-gallery` grids into clickable galleries
     with a shared fullscreen lightbox (legacy WordPress gallery behaviour).
  ----------------------------------------------------------------------- */
  (() => {
    const galleries = [
      ...document.querySelectorAll('.bst-main-page__content .bst-rte-gallery'),
      ...document.querySelectorAll('.bst-main-page__content .image-slider'),
    ];
    const groups = [];

    galleries.forEach((gallery) => {
      const itemSelector = gallery.classList.contains('image-slider') ? '.image-slider__slide' : '.bst-rte-gallery__item';
      const items = [...gallery.querySelectorAll(itemSelector)].filter((item) => item.querySelector('img'));
      if (!items.length) return;

      const groupIndex = groups.length;
      groups.push(items.map((item) => {
        const img = item.querySelector('img');
        return { src: img.currentSrc || img.src, alt: (img.getAttribute('alt') || '').trim() };
      }));

      gallery.classList.add('is-enhanced');
      items.forEach((item, index) => {
        const label = (item.querySelector('img').getAttribute('alt') || 'Bild').trim();
        item.setAttribute('role', 'button');
        item.setAttribute('tabindex', '0');
        item.setAttribute('aria-label', `${label} vergrößern`);

        const open = () => openLightbox(groupIndex, index);
        item.addEventListener('click', open);
        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          open();
        });
      });
    });

    if (!groups.length) return;

    const overlay = document.createElement('div');
    overlay.className = 'bst-rte-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Bildergalerie');
    overlay.hidden = true;
    overlay.innerHTML = `
      <button type="button" class="bst-rte-lightbox__btn bst-rte-lightbox__btn--close" aria-label="Schließen"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      <button type="button" class="bst-rte-lightbox__btn bst-rte-lightbox__btn--prev" aria-label="Vorheriges Bild"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button>
      <button type="button" class="bst-rte-lightbox__btn bst-rte-lightbox__btn--next" aria-label="Nächstes Bild"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg></button>
      <div class="bst-rte-lightbox__stage">
        <img class="bst-rte-lightbox__img" alt="">
        <p class="bst-rte-lightbox__caption"></p>
      </div>`;
    document.body.appendChild(overlay);

    const imgEl = overlay.querySelector('.bst-rte-lightbox__img');
    const captionEl = overlay.querySelector('.bst-rte-lightbox__caption');
    const btnClose = overlay.querySelector('.bst-rte-lightbox__btn--close');
    const btnPrev = overlay.querySelector('.bst-rte-lightbox__btn--prev');
    const btnNext = overlay.querySelector('.bst-rte-lightbox__btn--next');

    let activeGroup = 0;
    let activeIndex = 0;
    let lastFocused = null;

    const render = () => {
      const slides = groups[activeGroup];
      const slide = slides[activeIndex];
      imgEl.src = slide.src;
      imgEl.alt = slide.alt;
      captionEl.textContent = slide.alt;
      overlay.classList.toggle('bst-rte-lightbox--single', slides.length < 2);
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); closeLightbox(); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
    };

    function openLightbox(groupIndex, index) {
      activeGroup = groupIndex;
      activeIndex = index;
      lastFocused = document.activeElement;
      overlay.hidden = false;
      render();
      requestAnimationFrame(() => overlay.classList.add('is-open'));
      document.body.style.overflow = 'hidden';
      btnClose.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function closeLightbox() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      const done = (event) => {
        if (event && event.target !== overlay) return;
        overlay.hidden = true;
        imgEl.removeAttribute('src');
        overlay.removeEventListener('transitionend', done);
      };
      overlay.addEventListener('transitionend', done);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function step(delta) {
      const slides = groups[activeGroup];
      activeIndex = (activeIndex + delta + slides.length) % slides.length;
      render();
    }

    btnClose.addEventListener('click', closeLightbox);
    btnPrev.addEventListener('click', () => step(-1));
    btnNext.addEventListener('click', () => step(1));
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target.classList.contains('bst-rte-lightbox__stage')) closeLightbox();
    });
  })();

});
